/**
 * "Scheduling delegate" on a Sales Team position page (migration 1780). Everyone who can open the page sees who the
 * delegates are; only an admin / superadmin names or removes one (the database refuses anyone else).
 *
 * A scheduling delegate (an active MSR, Territory Manager or Regional Manager) gets this position's SCHEDULING rights for
 * its team: team availability, the team's events on the Sales Calendar, Confirm Schedule / Assign Rep, and the Lead rep.
 * Not: editing positions or territories, approving AD changes, or cancelling / archiving / re-dating events.
 */
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  grantSchedulingDelegate,
  listSchedulingDelegates,
  revokeSchedulingDelegate,
  searchSchedulingDelegateCandidates,
  type SchedulingDelegate,
} from "../../services/schedulingDelegates.service";
import Button from "../common/Button";
import Badge from "../ui/Badge";
import ConfirmDialog from "../ui/ConfirmDialog";
import { useToast } from "../ui/Toast";

const ROLE_LABEL: Record<string, string> = {
  msr: "MSR",
  territory_manager: "Territory Manager",
  regional_manager: "Regional Manager",
};

interface Props {
  positionId: string;
  positionLabel: string;
  isAdmin: boolean;
}

const SchedulingDelegatesCard: React.FC<Props> = ({
  positionId,
  positionLabel,
  isAdmin,
}) => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [note, setNote] = useState("");
  const [pendingRemove, setPendingRemove] = useState<SchedulingDelegate | null>(
    null,
  );

  const {
    data: delegates = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["scheduling-delegates", positionId],
    queryFn: () => listSchedulingDelegates(positionId),
  });
  const q = search.trim();
  const { data: candidates = [], isFetching: searching } = useQuery({
    queryKey: ["scheduling-delegate-candidates", q],
    queryFn: () => searchSchedulingDelegateCandidates(q),
    enabled: isAdmin && adding && q.length >= 2,
    staleTime: 30_000,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["scheduling-delegates", positionId] });
    qc.invalidateQueries({ queryKey: ["my-scheduling-delegations"] });
  };
  const grant = useMutation({
    mutationFn: (profileId: string) =>
      grantSchedulingDelegate(positionId, profileId, note),
    onSuccess: () => {
      refresh();
      setAdding(false);
      setSearch("");
      setNote("");
      showToast(
        "success",
        "Scheduling delegate added. They see the Sales Calendar for this team the next time they open the app.",
      );
    },
    onError: (e: Error) => showToast("error", e.message),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => revokeSchedulingDelegate(id),
    onSuccess: () => {
      refresh();
      setPendingRemove(null);
      showToast("success", "Scheduling delegate removed.");
    },
    onError: (e: Error) => {
      setPendingRemove(null);
      showToast("error", e.message);
    },
  });

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 p-6"
      data-testid="scheduling-delegates-card"
    >
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold text-gray-900">
          Scheduling delegate
        </h2>
        {isAdmin && !adding && (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            Add scheduling delegate
          </Button>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-4">
        A scheduling delegate schedules this position's team for {positionLabel}
        : they see the team's availability and events on the Sales Calendar,
        assign reps, confirm schedules and set the Lead rep. They cannot edit
        positions or territories, approve AD changes, or cancel, archive or
        re-date events. Their own role does not change.
      </p>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-700">{(error as Error).message}</p>
      ) : delegates.length === 0 ? (
        <p
          className="text-sm text-gray-500"
          data-testid="scheduling-delegate-none"
        >
          No scheduling delegate.
        </p>
      ) : (
        <ul className="space-y-2">
          {delegates.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2"
              data-testid="scheduling-delegate-row"
            >
              <div className="text-sm">
                <span className="font-medium text-gray-900">
                  Scheduling delegate: {d.delegate_name}
                </span>
                <span className="ml-2 text-gray-500">
                  {ROLE_LABEL[d.delegate_role] ?? d.delegate_role}
                </span>
                {!d.is_effective && (
                  <Badge variant="warning" size="sm" className="ml-2">
                    Suspended (inactive or role changed)
                  </Badge>
                )}
                <div className="text-xs text-gray-500">
                  Added {new Date(d.granted_at).toLocaleDateString()}
                  {d.granted_by_name ? ` by ${d.granted_by_name}` : ""}
                  {d.note ? ` · ${d.note}` : ""}
                </div>
              </div>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => setPendingRemove(d)}
                  disabled={revoke.isPending}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {isAdmin && adding && (
        <div className="mt-4 rounded-lg border border-gray-200 p-3 space-y-2">
          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="sched-delegate-search"
          >
            Find an MSR, Territory Manager or Regional Manager
          </label>
          <input
            id="sched-delegate-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name or email (2+ letters)"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            autoFocus
          />
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            placeholder="Note (optional), e.g. why they schedule this team"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          {q.length >= 2 && (
            <ul
              className="max-h-60 overflow-y-auto divide-y divide-gray-100"
              data-testid="scheduling-delegate-candidates"
            >
              {searching && candidates.length === 0 && (
                <li className="py-2 text-sm text-gray-400">Searching…</li>
              )}
              {!searching && candidates.length === 0 && (
                <li className="py-2 text-sm text-gray-500">
                  No active MSR, Territory Manager or Regional Manager matches.
                </li>
              )}
              {candidates.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between py-2"
                >
                  <div className="text-sm">
                    <span className="font-medium text-gray-900">
                      {c.full_name}
                    </span>
                    <span className="ml-2 text-gray-500">
                      {ROLE_LABEL[c.role] ?? c.role}
                      {c.email ? ` · ${c.email}` : ""}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => grant.mutate(c.id)}
                    disabled={grant.isPending}
                  >
                    Make delegate
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setAdding(false);
                setSearch("");
                setNote("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={pendingRemove !== null}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => pendingRemove && revoke.mutate(pendingRemove.id)}
        title="Remove this scheduling delegate?"
        message={`${pendingRemove?.delegate_name ?? "They"} will no longer see this team's Sales Calendar or be able to assign its reps. Reps they already assigned stay assigned.`}
        confirmText="Remove"
        variant="danger"
        isLoading={revoke.isPending}
      />
    </div>
  );
};

export default SchedulingDelegatesCard;
